import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, Lock, LockOpen, MessageCircle, Send } from 'lucide-react';
import type * as Y from 'yjs';
import {
  FRIEND_MAX_ACTIVE_GAMES,
  GAME_CHAT_KEY,
  GAME_CHAT_MAX,
  GAME_MOVES_KEY,
  gameChatMessageSchema,
  gameDocName,
  summarizeGameTurn,
  type GameChatMessage,
  type GameMeta,
} from '@awwyeah/shared';
import { useYArray, useYDoc } from '../../../sync';
import { challengeQuip, startErrorCopy } from '../friendLounge';
import { newId, nowIso } from '../gameOps';
import { apiStartFriendGame, GuestLinkApiError } from '../guestApi';
import type { CurrentPlayer } from '../guestPlayer';
import { sealedNoteAnnouncement } from '../sealedNote';
import { useGameRoster } from '../useHouseholdRoster';
import {
  isGameSoundOn,
  playChatPop,
  playMoveArrived,
  setGameSoundOn,
  unlockGameAudio,
} from '../sound';
import { Sheet } from './Sheet';
import { parseChat, readChatSeen, unreadFromOthers, writeChatSeen } from './chatSeen';
import { OpenNoteForm, SealNoteForm } from './SealedNotePanel';

/** Quick lines for one-tap smack talk. Friendly ribbing, nothing about time or turns owed. */
const QUICK_LINES = ['Nice one 😏', 'Lucky! 🍀', 'Watch this…', 'Oof 😬', 'Ha! 😂', 'GG 🤝'];

/** Opening a game syncs its history in — that's not "a move just arrived". */
const SETTLE_MS = 2000;

function buzz(ms: number): void {
  try {
    navigator.vibrate?.(ms);
  } catch {
    // no haptics
  }
}

/**
 * Everything every board gets for free, wrapped around whichever kind is showing:
 *
 *  - **"They moved."** When the other player's move lands while you're looking at the game
 *    (a remote change to the move log, page visible), the board gets a soft glow, a small
 *    "Faye moved" chip, a ding (if sounds are on) and a tiny buzz. Your own moves and the
 *    history syncing in when you open a game don't count.
 *  - **Chat.** A little smack-talk thread stored in the match doc, so a guest player can
 *    join in. Players write; spectators read. No pushes for chat, ever: it's there when you
 *    look, never a reason to look.
 *  - **Sealed notes** (friend of the family): a player can pass a short secret through a
 *    Handoff mailbox; the chat only says one exists, the magic words travel out of band.
 *  - **Play again, for a guest.** Members have each board's Rematch; a guest can't write
 *    the registry, so once a game is decided they get "play again" / "pick another game",
 *    which the server creates for them (`POST /api/games/friend/games`).
 */
export function GameTable({
  meta,
  player,
  children,
}: {
  meta: GameMeta;
  player: CurrentPlayer;
  children: ReactNode;
}): JSX.Element {
  const doc = useYDoc(gameDocName(meta.id));
  const roster = useGameRoster(meta.guestNames);
  const isPlayer = meta.playerIds.includes(player.id);
  const opponentId = meta.playerIds.find((id) => id !== player.id) ?? meta.playerIds[1];
  const opponentName = roster.getMember(opponentId).name;

  // ---- Move arrivals.
  const [pulse, setPulse] = useState(0);
  const mountedAt = useRef(Date.now());
  useEffect(() => {
    const moves = doc.getArray<unknown>(GAME_MOVES_KEY);
    const onChange = (event: Y.YArrayEvent<unknown>, tr: Y.Transaction): void => {
      if (tr.local) return;
      if (Date.now() - mountedAt.current < SETTLE_MS) return;
      const added = event.changes.added.size;
      if (added === 0) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      setPulse((n) => n + 1);
      playMoveArrived();
      buzz(18);
    };
    moves.observe(onChange);
    return () => moves.unobserve(onChange);
  }, [doc]);

  const [chipVisible, setChipVisible] = useState(false);
  useEffect(() => {
    if (pulse === 0) return undefined;
    setChipVisible(true);
    const t = setTimeout(() => setChipVisible(false), 2600);
    return () => clearTimeout(t);
  }, [pulse]);

  // ---- Decided? (for a guest's "play again" — members have each board's own Rematch.)
  const { items: rawMoves } = useYArray<unknown>(doc, GAME_MOVES_KEY);
  const decided = useMemo(
    () => meta.status === 'done' || summarizeGameTurn(meta.kind, rawMoves, meta.playerIds).decided,
    [meta.status, meta.kind, meta.playerIds, rawMoves],
  );

  // ---- Chat.
  const { array: chatArray, items: rawChat } = useYArray<unknown>(doc, GAME_CHAT_KEY);
  const chat = useMemo(() => parseChat(rawChat), [rawChat]);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(() => readChatSeen(meta.id));
  const unreadList = unreadFromOthers(chat, seen, player.id);
  const unread = unreadList.length;
  const [toast, setToast] = useState<GameChatMessage | null>(null);
  const [soundOn, setSoundOn] = useState(isGameSoundOn);

  useEffect(() => {
    if (!open) return;
    setSeen(chat.length);
    writeChatSeen(meta.id, chat.length);
  }, [open, chat.length, meta.id]);

  useEffect(() => {
    const onChange = (event: Y.YArrayEvent<unknown>, tr: Y.Transaction): void => {
      if (tr.local || Date.now() - mountedAt.current < SETTLE_MS) return;
      const latest = gameChatMessageSchema.safeParse(
        chatArray.get(chatArray.length - 1) as unknown,
      );
      if (!latest.success || event.changes.added.size === 0) return;
      if (latest.data.authorId === player.id) return; // your own line from another device
      playChatPop();
      if (!open) setToast(latest.data);
    };
    chatArray.observe(onChange);
    return () => chatArray.unobserve(onChange);
  }, [chatArray, open, player.id]);

  // Opening a game with something new waiting: show the newest line once, after the
  // history has synced in.
  const greeted = useRef(false);
  const latestUnread = unreadList[unreadList.length - 1] ?? null;
  useEffect(() => {
    if (greeted.current || !latestUnread) return undefined;
    const t = setTimeout(() => {
      greeted.current = true;
      setToast(latestUnread);
    }, 900);
    return () => clearTimeout(t);
  }, [latestUnread]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const [draft, setDraft] = useState('');
  const send = (text: string): void => {
    const clean = text.trim().slice(0, GAME_CHAT_MAX);
    if (!clean || !isPlayer) return;
    unlockGameAudio();
    chatArray.push([{ id: newId(), authorId: player.id, text: clean, at: nowIso() }]);
    setDraft('');
  };

  // ---- Sealed notes. The announcement is the only trace in the match doc — never the code.
  const [noteMode, setNoteMode] = useState<'seal' | 'open' | null>(null);
  const announceSealedNote = (): void => {
    if (!isPlayer) return;
    chatArray.push([
      {
        id: newId(),
        authorId: player.id,
        text: sealedNoteAnnouncement(player.name),
        at: nowIso(),
        sealed: true,
      },
    ]);
  };

  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [open, chat.length]);

  return (
    <div className="relative pb-4" onPointerDown={unlockGameAudio}>
      <style>{`@keyframes awwyeah-move-glow { 0% { box-shadow: 0 0 0 0 rgba(255,196,90,0), inset 0 0 0 0 rgba(255,196,90,0) } 25% { box-shadow: 0 0 0 6px rgba(255,196,90,.45), inset 0 0 40px 4px rgba(255,196,90,.25) } 100% { box-shadow: 0 0 0 14px rgba(255,196,90,0), inset 0 0 0 0 rgba(255,196,90,0) } }
@keyframes awwyeah-chip-in { 0% { transform: translate(-50%, -8px); opacity: 0 } 15% { transform: translate(-50%, 0); opacity: 1 } 85% { opacity: 1 } 100% { opacity: 0 } }`}</style>

      {children}

      {player.isGuest && isPlayer && decided && (
        <GuestPlayAgain meta={meta} opponentId={opponentId} opponentName={opponentName} />
      )}

      {pulse > 0 && (
        <div
          key={pulse}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-xl"
          style={{ animation: 'awwyeah-move-glow 1400ms ease-out both' }}
          data-testid="game-move-pulse"
        />
      )}
      {chipVisible && (
        <div
          role="status"
          className="pointer-events-none fixed left-1/2 top-16 z-30 flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 text-sm font-semibold text-fg shadow-lg"
          style={{ animation: 'awwyeah-chip-in 2600ms ease-out both' }}
          data-testid="game-move-chip"
        >
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: roster.getMember(opponentId).color }}
          />
          {isPlayer ? `${opponentName} moved` : 'New move'}
        </div>
      )}

      {toast && !open && (
        <button
          type="button"
          onClick={() => {
            setToast(null);
            setOpen(true);
          }}
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-30 max-w-[16rem] rounded-2xl rounded-br-sm border border-line bg-surface px-3 py-2 text-left text-sm text-fg shadow-lg"
          data-testid="game-chat-toast"
        >
          <span className="font-semibold">{roster.getMember(toast.authorId).name}: </span>
          {toast.text}
        </button>
      )}

      <button
        type="button"
        aria-label={unread > 0 ? `Table talk, ${unread} new` : 'Table talk'}
        onClick={() => setOpen(true)}
        className={`mx-auto mt-4 flex h-12 w-full max-w-[22rem] items-center justify-center gap-2 rounded-full border text-base font-semibold ${
          unread > 0 ? 'border-transparent bg-inverse text-inverse-fg' : 'border-line text-fg-2'
        }`}
        data-testid="game-chat-button"
      >
        <MessageCircle size={20} aria-hidden="true" />
        Table talk
        {unread > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#e2574c] px-1.5 text-[11px] font-bold text-white">
            {unread} new
          </span>
        )}
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title="Table talk" tall testId="game-chat">
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div
            ref={listRef}
            className="flex max-h-[45vh] min-h-[8rem] flex-col gap-2 overflow-y-auto"
          >
            {chat.length === 0 && (
              <p className="py-6 text-center text-sm text-fg-3">
                {isPlayer ? 'Say something to the other side of the table.' : 'Nothing said yet.'}
              </p>
            )}
            {chat.map((m) => {
              const mine = m.authorId === player.id;
              const who = roster.getMember(m.authorId);
              return (
                <div
                  key={m.id}
                  className={`flex max-w-[85%] flex-col ${mine ? 'self-end items-end' : 'self-start items-start'}`}
                  data-testid="game-chat-message"
                >
                  {!mine && <span className="px-1 text-xs text-fg-3">{who.name}</span>}
                  {m.sealed ? (
                    <span
                      className="flex flex-col gap-2 rounded-2xl border border-dashed border-line-strong bg-raised px-3 py-2 text-sm text-fg"
                      data-testid="game-chat-sealed"
                    >
                      <span>{m.text}</span>
                      {!mine && isPlayer && (
                        <button
                          type="button"
                          onClick={() => setNoteMode('open')}
                          className="flex h-9 items-center justify-center gap-1.5 self-start rounded-full bg-inverse px-3 text-xs font-semibold text-inverse-fg"
                        >
                          <LockOpen size={13} aria-hidden="true" /> Open it
                        </button>
                      )}
                    </span>
                  ) : (
                    <span
                      className={`rounded-2xl px-3 py-2 text-base ${mine ? 'rounded-br-sm text-white' : 'rounded-bl-sm bg-raised text-fg'}`}
                      style={
                        mine
                          ? { backgroundColor: who.color }
                          : { borderLeft: `3px solid ${who.color}` }
                      }
                    >
                      {m.text}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {isPlayer && noteMode === 'seal' && (
            <SealNoteForm
              authorName={player.name}
              onSealed={announceSealedNote}
              onCancel={() => setNoteMode(null)}
            />
          )}
          {isPlayer && noteMode === 'open' && <OpenNoteForm onClose={() => setNoteMode(null)} />}

          {isPlayer && noteMode === null ? (
            <>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_LINES.map((line) => (
                  <button
                    key={line}
                    type="button"
                    onClick={() => send(line)}
                    className="h-9 rounded-full border border-line px-3 text-sm text-fg-2"
                  >
                    {line}
                  </button>
                ))}
              </div>
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  send(draft);
                }}
              >
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  maxLength={GAME_CHAT_MAX}
                  placeholder={`Say something to ${opponentName}…`}
                  className="h-12 min-w-0 flex-1 rounded-full border border-line bg-surface px-4 text-base text-fg"
                  data-testid="game-chat-input"
                />
                <button
                  type="submit"
                  aria-label="Send"
                  disabled={!draft.trim()}
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-inverse text-inverse-fg disabled:opacity-40"
                >
                  <Send size={18} aria-hidden="true" />
                </button>
              </form>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNoteMode('seal')}
                  className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full border border-line text-sm text-fg-2"
                  data-testid="sealed-note-button"
                >
                  <Lock size={14} aria-hidden="true" /> Pass a sealed note
                </button>
                <button
                  type="button"
                  onClick={() => setNoteMode('open')}
                  className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full border border-line text-sm text-fg-2"
                  data-testid="open-note-button"
                >
                  <LockOpen size={14} aria-hidden="true" /> Open a sealed note
                </button>
              </div>
            </>
          ) : !isPlayer ? (
            <p className="text-center text-sm text-fg-3">Only the players can chat here.</p>
          ) : null}

          <button
            type="button"
            onClick={() => {
              const next = !soundOn;
              setGameSoundOn(next);
              setSoundOn(next);
              if (next) {
                unlockGameAudio();
                playMoveArrived();
              }
            }}
            aria-pressed={soundOn}
            className="flex h-11 items-center justify-center gap-2 text-sm text-fg-2"
            data-testid="game-sound-toggle"
          >
            {soundOn ? <Bell size={16} /> : <BellOff size={16} />}
            {soundOn ? 'Ding when they move or chat: on' : 'Ding when they move or chat: off'}
          </button>
        </div>
      </Sheet>
    </div>
  );
}

/**
 * A guest's way to keep playing once a game is decided (friend of the family). Same kind
 * again with the same person, or off to the lounge to pick something else. The guest moves
 * first in the new game — they asked.
 */
function GuestPlayAgain({
  meta,
  opponentId,
  opponentName,
}: {
  meta: GameMeta;
  opponentId: string;
  opponentName: string;
}): JSX.Element {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const again = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const seat = await apiStartFriendGame(meta.kind, opponentId);
      const id = seat.game?.id;
      if (!id) throw new Error('no game');
      navigate(`/play/g/${encodeURIComponent(id)}`, {
        state: { quip: challengeQuip(opponentName, id) },
      });
    } catch (caught) {
      const code = caught instanceof GuestLinkApiError ? caught.code : 'unknown';
      setError(startErrorCopy(code, FRIEND_MAX_ACTIVE_GAMES));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mx-auto mt-4 flex w-full max-w-[22rem] flex-col gap-2 rounded-xl border border-line p-3 text-center"
      data-testid="guest-play-again"
    >
      <p className="text-sm font-semibold text-fg">Good game. Again? 🤝</p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => void again()}
          className="h-11 flex-1 rounded-full bg-inverse text-sm font-semibold text-inverse-fg disabled:opacity-50"
          data-testid="guest-play-again-button"
        >
          {busy ? 'Sending…' : 'Play again'}
        </button>
        <button
          type="button"
          onClick={() => navigate('/play/lounge')}
          className="h-11 flex-1 rounded-full border border-line text-sm font-semibold text-fg-2"
          data-testid="guest-pick-another-button"
        >
          Pick another game
        </button>
      </div>
      {error && (
        <p className="text-sm text-fg" role="status">
          {error}
        </p>
      )}
    </div>
  );
}
